import { useState, useEffect } from 'react';
import firebase from 'firebase/app';

/**
 * Subscribes to /clients/strike/admins, then fetches user + client data for
 * each admin. The collection is small so individual gets are fine.
 *
 * Returns [loading, admins] where each admin is:
 *   { userId, role, user: { ...userDoc } | null, client: { ...clientDoc } | null }
 */
export function useStrikeAdmins() {
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState(null);

  useEffect(() => {
    setLoading(true);
    const db = firebase.firestore();

    const unsubscribe = db
      .collection('clients')
      .doc('strike')
      .collection('admins')
      .onSnapshot(
        async (snap) => {
          const adminDocs = snap.docs.map((doc) => ({
            userId: doc.id,
            role: doc.data().role || null,
          }));

          if (adminDocs.length === 0) {
            setAdmins([]);
            setLoading(false);
            return;
          }

          const enriched = await Promise.all(
            adminDocs.map(async (admin) => {
              const [userSnap, clientSnap] = await Promise.all([
                db.collection('users').doc(admin.userId).get(),
                db.collection('clients').where('ownerId', '==', admin.userId).limit(1).get(),
              ]);

              const user = userSnap.exists ? { id: userSnap.id, ...userSnap.data() } : null;
              const client = clientSnap.empty
                ? null
                : { id: clientSnap.docs[0].id, ...clientSnap.docs[0].data() };

              return { ...admin, user, client };
            }),
          );

          setAdmins(enriched);
          setLoading(false);
        },
        (err) => {
          console.error('[useStrikeAdmins] Failed to load admins:', err);
          setAdmins([]);
          setLoading(false);
        },
      );

    return () => unsubscribe();
  }, []);

  return [loading, admins];
}
